
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace, State, Getter, Mutation, Action } from "vuex-class";

import {
	axiosJsonInstance
} from "@/axios.instance";
import { cloneDeep } from "lodash";

import { AlarmQueueFilterID, AlarmQueueFilter } from "@/store/eventqueue/types";
import { DispatchRequest, MobileFeatures } from "@/store/mobile/types";
import {
	Guard,
	GuardTour,
	DispatchableEvent,
	MapOptions,
	DispatchStatus,
	GuardsByStatus,
	EventStatus,

	MapFilter,
	DispatchMap,
	Sidebar,
	GuardList,
	ToursList,

	DispatchApi,
	DispatchUpdateService
} from "@sureview/v2-dispatch"

import {
	convertStringToLatLng,
	ILatLngBounds,
	ILatLng
} from "@sureview/v2-mapping";

import SureViewIcon from "@/components/SureViewIcon.vue";

import { darkMapStyle, satelliteMapStyle, getMapStyle } from "@/scripts/mapping/mapStyle";

const Mobile = namespace("mobile");
const Eventqueue = namespace("eventqueue");

import api from "@/services/api.service";
import mobileApi from "@/services/api.mobile.service";
const dispatchApi = new DispatchApi(axiosJsonInstance);

@Component({
	components: {
		"sureview-icon": SureViewIcon
	}
})
export default class DispatchManageMap extends Vue {
	$refs: {
		dispatchMap: DispatchMap
	}

	@State("User") public currentUser: {
		userId: number,
		fullName: string;
	};

	
	@Getter("getMapKey") private mapKey: string;

	// Feature flag
	@Mobile.Getter("getIsRightSidebarOpen") isRightSidebarOpen: boolean;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	@Mobile.State("ManageDataLoading") public loading: boolean;
	@Mobile.State("ManageGuards") public guards: Guard[];
	@Mobile.State("ManageEvents") public alarms: DispatchableEvent[];
	@Mobile.State("ManageAlarmQueueFilters") public alarmQueueFilters: AlarmQueueFilter[];
	@Mobile.State("ManageCurrentAlarmQueueFilters") public currentFilters: AlarmQueueFilter[];

	@Mobile.Mutation("setManageDataLoading") public setLoading: (load: boolean) => void;
	@Mobile.Mutation("setManageGuards") setGuards: (guards: Guard[]) => void;
	@Mobile.Mutation("setManageEvents") setEvents: (guards: Guard[]) => void;
	@Mobile.Mutation("setManageFilters") setFilters: (filters: AlarmQueueFilter[]) => void;
	@Mobile.Mutation("setManageCurrentFilters") setCurrentFilters: (filters: AlarmQueueFilter[]) => void;
	@Mobile.Mutation setMapGoto: (gotoFunction: (latLng: ILatLng) => void) => void;
	@Mobile.Mutation toggleRightSidebarVisibility: () => void;

	@Eventqueue.Action loadAvailableAlarmQueueFilters!: () => Promise<AlarmQueueFilter[]>;

	mapOptions: MapOptions = {
			showAlarms: true,
			showGuardLinks: true,
			showTourCheckpoints: true,
			showTourLinks: true
		};
	statusVisibility: { [key in DispatchStatus]: boolean } = {
			1: true,
			2: true,
			3: true,
			4: true,
			5: true,
			6: true
		};

	filterBounds: { [alarmQueueFilterId: number]: ILatLngBounds } = {};
	updateService: DispatchUpdateService = null;
	destroyed = false;

	public guardTours: GuardTour[] = [];
	patrolZones: any[] = [];

	public get mapStyle () {
		return darkMapStyle;
	}

	public get isDispatchEnabled() {
		return this.getFeature(["Dispatch"]) && this.getFeature(["Mobile", "ManageDispatch"]);
	}

	get currentAlarmQueueFilters(): AlarmQueueFilter[]
	{
		return this.currentFilters;
	}
	set currentAlarmQueueFilters(value: AlarmQueueFilter[])
	{
		this.setCurrentFilters(value);
	}

	get requestFilters(): AlarmQueueFilterID[] {
		return this.currentAlarmQueueFilters.map(a => {
				return {
					filterId: a.alarmQueueFilterID,
					regionId: a.regionID
				};
			});
	}

	public async mounted() {
		this.setMapGoto((latLng: ILatLng) => {
			if (this.isRightSidebarOpen) {
				this.toggleRightSidebarVisibility();
			}
			if (this.$refs.dispatchMap) {
				this.$refs.dispatchMap.goToLocation(latLng);
			}
		});

		this.destroyed = false;
		if (this.isDispatchEnabled) {
			this.setLoading(true);

			this.setFilters((await this.loadAvailableAlarmQueueFilters())
				.filter(f => f.isLocal));
			const userAlarmQueueFilters = ((await api.loadAlarmQueueFilters()).data)
				.filter(f => !f.regionID);

			this.currentAlarmQueueFilters = this.alarmQueueFilters
				.filter(f => userAlarmQueueFilters.find(userFilter => userFilter.alarmQueueFilterID === f.alarmQueueFilterID));

			this.updateService = new DispatchUpdateService(
				dispatchApi,
				() => this.requestFilters,
				guards => this.setGuards(cloneDeep(guards)),
				alarms => this.setEvents(cloneDeep(alarms)),
				null
			);

			if (!this.destroyed) {
				this.updateService.Start();
			}
			this.setLoading(false);
		}
	}

	public beforeDestroy() {
		this.destroyed = true;
		if (this.updateService) {
			this.updateService.Stop();
		}

		this.setGuards([]);
	}

	dispatchGuardToAlarm ({ guard, event, latLong }: { guard: Guard, event: DispatchableEvent, latLong: string }) {
		if (guard.status == DispatchStatus.Available) {
			guard.status = DispatchStatus.Dispatched;
		}

		mobileApi.addDispatchRequest(
			guard.guardId, 
			null, 
			event.eventId,
			this.currentUser.fullName, 
			"Dispatch to area and investigate", 
			latLong, 
			"" // Replacing value with empty string here - API will look up correct value
		);
	}

	async clearGuardStatus (guard: Guard) {
		if (guard.status === DispatchStatus.OnBreak) {
			await dispatchApi.guardMakeAvailable(guard.guardId);
		} else {
			const queue = await dispatchApi.dispatchQueue(guard.guardId);
			const activeEvent = queue.find(q => q.active);
			if (activeEvent) {
				dispatchApi.dispatchFinish({
					guardId: guard.guardId,
					dispatchQueueId: activeEvent.dispatchQueueID
				});
			}
		}
	}

	@Watch("currentAlarmQueueFilters")
	async alarmQueueFiltersChanged(newFilters: AlarmQueueFilter[]) {
		if (newFilters.length === 0) {
			if (this.loading) {
				this.setLoading(false);
			}
			return;
		}

		const loadFilterBounds = newFilters.filter(f => !this.filterBounds[f.alarmQueueFilterID])
			.map(f => api.loadAlarmQueueFilterBounds(f.alarmQueueFilterID).then(bounds => {
				return this.filterBounds[f.alarmQueueFilterID] = bounds.data;
			}));

		if (loadFilterBounds.length > 0) {
			await Promise.all(loadFilterBounds);
		}

		const combinedBounds = {
			north: null,
			south: null,
			east: null,
			west: null
		}

		newFilters.forEach(filter => {
			var bounds = this.filterBounds[filter.alarmQueueFilterID];
			if (bounds.anyBounds) {
				combinedBounds.north = combinedBounds.north === null ? bounds.north : Math.max(bounds.north, combinedBounds.north);
				combinedBounds.east = combinedBounds.east === null ? bounds.east : Math.max(bounds.east, combinedBounds.east);

				combinedBounds.south = combinedBounds.south === null ? bounds.south : Math.min(bounds.south, combinedBounds.south);
				combinedBounds.west = combinedBounds.west === null ? bounds.west : Math.min(bounds.west, combinedBounds.west);
			}
		});

		if (combinedBounds.north !== null) {
			this.$refs.dispatchMap.gotoBounds(combinedBounds);
		}

		this.patrolZones = await dispatchApi.getPatrolZonesForAlarmQueueFilters(newFilters.map(f => {
			return {
				filterId: f.alarmQueueFilterID,
				regionId: f.regionID
			};
		}));
	}
}
